import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CalendarBaseProps {
  tipo?: 'day' | 'month' | 'year';
  selected?: Date | null;
  onSelectDate: (date: Date) => void;
  minDate?: Date;
  withTime?: boolean;
  mode?: 'single' | 'range';
  maxRangeDays?: number;
}

type ViewMode = "days" | "months" | "years";

const CalendarBase: React.FC<CalendarBaseProps> = ({
  selected,
  onSelectDate,
  minDate,
  withTime = false,
  tipo = 'day',
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>(tipo === 'month' ? 'months' : 'days');
  const [currentDate, setCurrentDate] = useState<Date>(selected || new Date());
  const [hour, setHour] = useState<string>(""); // Alterado para vazio inicialmente
  const [minute, setMinute] = useState<string>("");
  const [range, setRange] = useState<Date[]>([]);
  const timeInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) {
      setHour(String(selected.getHours()).padStart(2, "0"));
      setMinute(String(selected.getMinutes()).padStart(2, "0"));
    }
  }, [selected]);

  const daysOfWeek = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

  const changeMonth = (amount: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + amount);
    setCurrentDate(newDate);
  };

  const changeYear = (amount: number) => {
    const newDate = new Date(currentDate);
  
    if (viewMode === "years") {
      const currentYear = newDate.getFullYear();
      const base = currentYear - (currentYear % 12);
      newDate.setFullYear(base + amount * 12);
    } else {
      newDate.setFullYear(newDate.getFullYear() + amount);
    }
  
    setCurrentDate(newDate);
  };
  

  const handleSelectDay = (day: number) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

    if (withTime) {
      newDate.setHours(Number(hour || "0"));  // Verifica se o valor está vazio e usa 0
      newDate.setMinutes(Number(minute || "0"));
    }

    onSelectDate(newDate);
  };

  const handleSelectMonth = (month: number) => {
    if (tipo === 'month') {
      const newDate = new Date(currentDate.getFullYear(), month, 1);
      onSelectDate(newDate);
      return;
    }
    const newDate = new Date(currentDate.getFullYear(), month, 1);
    setCurrentDate(newDate);
    setViewMode("days");
  };

  const handleSelectYear = (year: number) => {
    if (tipo === 'year') {
      const newDate = new Date(year, currentDate.getMonth(), 1);
      onSelectDate(newDate);
      return;
    }
    const newDate = new Date(year, currentDate.getMonth(), 1);
    setCurrentDate(newDate);
    setViewMode("months");
  };
  const renderDayCell = (date: Date, isOtherMonth: boolean) => {
    const isSelected =
      selected &&
      selected.getDate() === date.getDate() &&
      selected.getMonth() === date.getMonth() &&
      selected.getFullYear() === date.getFullYear();
  
    return (
      <div
        key={date.toISOString()}
        className={`calendar-cell day ${isSelected ? "selected" : ""} ${isOtherMonth ? "other-month" : ""}`}
        onClick={() => handleSelectDay(date.getDate())}
      >
        {date.getDate()}
      </div>
    );
  };
  
  const renderDays = () => {
    const days = [];
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  
    let startDay = firstDayOfMonth.getDay()+1; // 0 (domingo) a 6 (sábado)
    if (startDay === 0) startDay = 7; // trata domingo como último
  
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const prevMonthDays = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
  
    // Dias do mês anterior
    for (let i = startDay - 1; i > 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, prevMonthDays - i + 1);
      days.push(renderDayCell(date, true));
    }
  
    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      days.push(renderDayCell(date, false));
    }
  
    // Dias do próximo mês (só o necessário para fechar a última semana)
    const totalCells = days.length;
    const nextDays = (7 - (totalCells % 7)) % 7;
  
    for (let i = 1; i <= nextDays; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i);
      days.push(renderDayCell(date, true));
    }
  
    return days;
  };
  
  

  const renderMonths = () => {
    const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  
    return (
      <div className="calendar-grid months">
        {months.map((month, idx) => {
          const isSelected =
            selected &&
            selected.getMonth() === idx &&
            selected.getFullYear() === currentDate.getFullYear();
  
          return (
            <div
              key={idx}
              className={`calendar-cell month ${isSelected ? "selected" : ""}`}
              onClick={() => handleSelectMonthCustom(idx)}
            >
              {month}
            </div>
          );
        })}
      </div>
    );
  };
  
  const renderYears = () => {
    const currentYear = currentDate.getFullYear();
    const startYear = currentYear - (currentYear % 12);
  
    return (
      <div className="calendar-grid years">
        {Array.from({ length: 12 }).map((_, idx) => {
          const year = startYear + idx;
          const isSelected = selected?.getFullYear() === year;
  
          return (
            <div
              key={year}
              className={`calendar-cell year ${isSelected ? "selected" : ""}`}
              onClick={() => handleSelectYearCustom(year)}
            >
              {year}
            </div>
          );
        })}
      </div>
    );
  };
  

  // Permite editar os campos de hora e minuto, sem bloquear a digitação
  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos
    if (value.length > 2) value = value.slice(0, 2); // Limita a 2 caracteres

    // Apenas valida se o valor não for maior que 23
    if (value !== "" && (Number(value) > 23)) return;

    setHour(value); // Deixa vazio ou atualiza com a hora digitada
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos
    if (value.length > 2) value = value.slice(0, 2); // Limita a 2 caracteres

    // Apenas valida se o valor não for maior que 59
    if (value !== "" && (Number(value) > 59)) return;

    setMinute(value); // Deixa vazio ou atualiza com o minuto digitado
  };

  const handleBlurTime = () => {
    const newDate = new Date(currentDate);
    newDate.setHours(Number(hour || "0"));  // Usa 0 caso o valor de hora seja vazio
    newDate.setMinutes(Number(minute || "0")); // Usa 0 caso o valor de minuto seja vazio
    onSelectDate(newDate);
  };
  

  
  const handleSelectMonthCustom = (month: number) => {
    if (tipo === 'month') {
      const newDate = new Date(currentDate.getFullYear(), month, 1);
      onSelectDate(newDate);
    } else {
      handleSelectMonth(month);
    }
  };

  const handleSelectYearCustom = (year: number) => {
    if (tipo === 'year') {
      const newDate = new Date(year, 0, 1);
      onSelectDate(newDate);
    } else {
      handleSelectYear(year);
    }
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <span onClick={() => viewMode === "days" ? changeMonth(-1) : changeYear(-1)}>{"<"}</span>
        <div onClick={() => {
          if (viewMode === "days") setViewMode("months");
          else if (viewMode === "months") setViewMode("years");
        }}>
          {viewMode === "days" && format(currentDate, "MMM yyyy", { locale: ptBR }).replace(/^\w/, (c) => c.toUpperCase())}
          {viewMode === "months" && format(currentDate, "yyyy", { locale: ptBR })}
          {viewMode === "years" && `${currentDate.getFullYear() - (currentDate.getFullYear() % 12)} - ${currentDate.getFullYear() - (currentDate.getFullYear() % 12) + 11}`}
        </div>
        <span onClick={() => viewMode === "days" ? changeMonth(1) : changeYear(1)}>{">"}</span>
      </div>

      <div className={`calendar-grid ${
        viewMode === "months" ? "" : viewMode === "years" ? "" : "days"
      }`}>

        {viewMode === "days" && (
          <>
            {daysOfWeek.map((day) => (
              <div key={day} className="calendar-cell weekday">
                {day}
              </div>
            ))}
          </>
        )}
        {viewMode === "days" && renderDays()}
        {viewMode === "months" && renderMonths()}
        {viewMode === "years" && renderYears()}
      </div>

      {withTime && (
        <div ref={timeInputRef} className="calendar-time-input">
          <input
            type="text"
            value={hour}
            onChange={handleHourChange}
            onBlur={handleBlurTime}
            maxLength={2}
            placeholder="HH"
            inputMode="numeric"
            className="time-field"
          />
          <span className="time-separator">:</span>
          <input
            type="text"
            value={minute}
            onChange={handleMinuteChange}
            onBlur={handleBlurTime}
            maxLength={2}
            placeholder="MM"
            inputMode="numeric"
            className="time-field"
          />
        </div>
      )}
    </div>
  );
};

export default CalendarBase;
